import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  useMediaQuery,
  Card,
  Fade,
  Grow
} from '@mui/material';
import React, { useState, useEffect } from 'react'
import StarRateIcon from '@mui/icons-material/StarRate';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';

const HomeSection2 = () => {

    const isMobile = useMediaQuery("(max-width:768px)");
    const isTablet = useMediaQuery("(max-width:1024px)");
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    const statsData = [
        {
            icon: <StarRateIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
            value: "4.6",
            label: "Rating",
            subtitle: "11,467+ Reviews",
            color: "#FFD700",
            gradient: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
        },
        {
            icon: <SchoolIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
            value: "10K+",
            label: "Happy Students",
            subtitle: "Worldwide",
            color: "#4CAF50",
            gradient: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)"
        },
        {
            icon: <PersonIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
            value: "200+",
            label: "Expert Tutors",
            subtitle: "Qualified Teachers",
            color: "#FF6B6B",
            gradient: "linear-gradient(135deg, #FF6B6B 0%, #EE5A52 100%)"
        },
        {
            icon: <AssignmentIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
            value: "10K+",
            label: "Tests Conducted",
            subtitle: "Practice Sessions",
            color: "#9C27B0",
            gradient: "linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)"
        }
    ];

    return (
        <Box
            component="section"
            sx={{
                position: 'relative',
                py: { xs: 6, md: 8 },
                px: { xs: 2, md: 4 },
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                overflow: 'hidden',
                minHeight: { xs: '400px', md: '450px' },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 30% 70%, rgba(19, 84, 193, 0.05) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(13, 71, 161, 0.05) 0%, transparent 50%)',
                    pointerEvents: 'none',
                }
            }}
        >
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Section Header */}
                <Fade in={isVisible} timeout={800}>
                    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #1354C1 0%, #0d47a1 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                                mb: 2,
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 60,
                                    height: 3,
                                    background: 'linear-gradient(135deg, #1354C1 0%, #0d47a1 100%)',
                                    borderRadius: 2,
                                }
                            }}
                        >
                            Our Achievements
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '0.95rem', md: '1.1rem' },
                                maxWidth: 500,
                                mx: 'auto',
                                lineHeight: 1.5
                            }}
                        >
                            Trusted by thousands of students worldwide
                        </Typography>
                    </Box>
                </Fade>

                {/* Stats Cards */}
                <Card
                    sx={{
                        background: 'linear-gradient(135deg, #1354C1 0%, #0d47a1 100%)',
                        borderRadius: { xs: 4, md: 6 },
                        boxShadow: '0 25px 50px rgba(19, 84, 193, 0.35)',
                        overflow: 'hidden',
                        position: 'relative',
                        // minHeight: { xs: '220px', md: '250px' },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(45deg, transparent 49%, rgba(255, 255, 255, 0.05) 50%, transparent 51%)',
                            pointerEvents: 'none',
                        }
                    }}
                >
                    <Grid container sx={{padding: "30px"}}>
                        {statsData.map((stat, index) => (
                            <Grid 
                                key={index} 
                                item 
                                xs={6} 
                                md={3}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        right: 0,
                                        top: '20%',
                                        bottom: '20%',
                                        width: '1px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        display: {
                                            xs: index % 2 === 0 ? 'block' : 'none',
                                            md: index < 3 ? 'block' : 'none'
                                        }
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: '20%',
                                        right: '20%',
                                        height: '1px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        display: {
                                            xs: index < 2 ? 'block' : 'none',
                                            md: 'none'
                                        }
                                    }
                                }}
                            >
                                <Grow
                                    in={isVisible}
                                    timeout={1000 + (index * 200)}
                                    style={{ transformOrigin: '0 0 0' }}
                                >
                                    <Box
                                        sx={{
                                            p: { xs: 2, md: 3 },
                                            textAlign: 'center',
                                            color: '#fff',
                                            position: 'relative',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            minHeight: { xs: '130px', md: '150px' },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            width: '100%',
                                            height: '100%',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                '& .stat-icon': {
                                                    transform: 'scale(1.2) rotate(5deg)',
                                                },
                                                '& .stat-value': {
                                                    transform: 'scale(1.05)',
                                                }
                                            }
                                        }}
                                    >
                                        {/* Icon with background */}
                                        <Box
                                            className="stat-icon"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: { xs: 60, md: 70 },
                                                height: { xs: 60, md: 70 },
                                                borderRadius: '50%',
                                                background: stat.gradient,
                                                mb: 1.5,
                                                mx: 'auto',
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                                            }}
                                        >
                                            {React.cloneElement(stat.icon, { 
                                                sx: { 
                                                    fontSize: { xs: 30, md: 36 },
                                                    color: '#fff'
                                                }
                                            })}
                                        </Box>

                                        {/* Value */}
                                        <Typography
                                            className="stat-value"
                                            variant="h4"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: { xs: '20px', sm: '25px', md: '25px' },
                                                mb: 0.3,
                                                transition: 'all 0.3s ease',
                                                background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 100%)',
                                                backgroundClip: 'text',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                lineHeight: 1.1
                                            }}
                                        >
                                            {stat.value}
                                        </Typography>

                                        {/* Label */}
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: { xs: '13px', md: '20px' },
                                                mb: 0.2,
                                                opacity: 0.95,
                                                lineHeight: 1.2
                                            }}
                                        >
                                            {stat.label}
                                        </Typography>

                                        {/* Subtitle */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: { xs: '12px', md: '16px' },
                                                opacity: 0.8,
                                                fontWeight: 400,
                                                lineHeight: 1.3
                                            }}
                                        >
                                            {stat.subtitle}
                                        </Typography>

                                        {/* Hover effect overlay */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)',
                                                opacity: 0,
                                                transition: 'opacity 0.3s ease',
                                                pointerEvents: 'none',
                                                '.stat-container:hover &': {
                                                    opacity: 1,
                                                }
                                            }}
                                        />
                                    </Box>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Card>

                {/* Floating elements for decoration */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '15%',
                        right: '5%',
                        width: { xs: 60, md: 80 },
                        height: { xs: 60, md: 80 },
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)',
                        filter: 'blur(15px)',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                            '0%, 100%': {
                                transform: 'translateY(0px)',
                            },
                            '50%': {
                                transform: 'translateY(-20px)',
                            },
                        },
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '15%',
                        left: '5%',
                        width: { xs: 50, md: 70 },
                        height: { xs: 50, md: 70 },
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(46, 125, 50, 0.05) 100%)',
                        filter: 'blur(12px)',
                        animation: 'float 8s ease-in-out infinite reverse',
                    }}
                />
            </Container>
        </Box>
    )
}

export default HomeSection2